import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabGroup, TabButton } from '../../ui/Tabs/index.js';
import { formatPeriodTick } from './chart-utils.js';
import type { BugSeverityPoint, BugTrendUnit } from '../../../types/api.js';

/**
 * 重大度の表示順（重大な順）。凡例・ツールチップ・積み上げ（下→上）で共通。
 * レベル3が最重大（bug_rate_over_level2 が「レベル2以上」を追う既存KPIの序列と同じ）
 * @graph-connects none
 */
const SEVERITY_ORDER = [
  { key: 'level3', labelKey: 'reliability.severity.level3', color: 'var(--color-error)' },
  { key: 'level2', labelKey: 'reliability.severity.level2', color: 'var(--color-warning)' },
  { key: 'level1', labelKey: 'reliability.severity.level1', color: 'var(--color-accent-primary)' },
  { key: 'undecided', labelKey: 'reliability.severity.undecided', color: '#94a3b8' },
  { key: 'notIncident', labelKey: 'reliability.severity.notIncident', color: '#cbd5e1' },
] as const;

/**
 * 凡例・ツールチップの表示順（重大な順）。
 * recharts v3 は既定で value/name ソートするため、明示的な itemSorter で上書きする。
 * @graph-connects none
 */
const ITEM_RANK: Record<string, number> = {
  level3: 0,
  level2: 1,
  level1: 2,
  undecided: 3,
  notIncident: 4,
};

/**
 * dataKey → 重大度順 rank（未知キーは末尾）
 * @graph-connects none
 */
function rankOf(dataKey: unknown): number {
  return ITEM_RANK[String(dataKey)] ?? 99;
}

export interface BugSeverityChartProps {
  /** 重大度別バグ件数データ（unit に応じて月次または週次） */
  data: BugSeverityPoint[];
  /** 取得月数（説明文用） */
  months: number;
  /** 読み込み状態 */
  isLoading: boolean;
  /** 集計単位（月次/週次・月曜始まり） */
  unit: BugTrendUnit;
  /** 集計単位切り替え時のコールバック */
  onUnitChange: (unit: BugTrendUnit) => void;
  /** 期間クリック時のコールバック（月次: YYYY-MM / 週次: 週開始日 YYYY-MM-DD）。ドリルダウンモーダル表示用 */
  onPeriodClick?: (period: string) => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 信頼性ページの「重大度別インシデント」チャート。案件起因＋潜在バグ（重複・バグではない・bot起票除外）を月次/週次（月曜始まり）切り替え可能な期間×インシデントレベル（レベル3/2/1・未決定・インシデントではない）で積み上げ表示し、期間クリックでドリルダウンモーダルを開く
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects TabGroup [calls] 月次/週次トグル描画
 * @graph-connects ComposedChart [calls] 重大度別積み上げチャート描画
 * @graph-connects formatPeriodTick [calls] 週次X軸目盛りの短縮フォーマット
 */
export function BugSeverityChart({
  data,
  months,
  isLoading,
  unit,
  onUnitChange,
  onPeriodClick,
}: BugSeverityChartProps) {
  const { t } = useTranslation();

  const hasData = data.length > 0;

  const tooltipStyle = {
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
  };

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('reliability.severity.title')}
        description={t(
          unit === 'week'
            ? 'reliability.severity.descriptionWeekly'
            : 'reliability.severity.description',
          { months },
        )}
        action={
          <TabGroup ariaLabel={t('reliability.unitSwitch')}>
            <TabButton
              active={unit === 'month'}
              label={t('reliability.unit.month')}
              onClick={() => {
                onUnitChange('month');
              }}
            />
            <TabButton
              active={unit === 'week'}
              label={t('reliability.unit.week')}
              onClick={() => {
                onUnitChange('week');
              }}
            />
          </TabGroup>
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <div className="h-80" style={onPeriodClick ? { cursor: 'pointer' } : undefined}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 8, right: 16, left: 8, bottom: 5 }}
                onClick={(state) => {
                  const period = state?.activeLabel;
                  if (onPeriodClick && typeof period === 'string' && period.length > 0) {
                    onPeriodClick(period);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  // 週次は目盛りを MM-DD に短縮する（年跨ぎ考慮は formatPeriodTick 側。ツールチップは全体表示）
                  tickFormatter={(v: string) => formatPeriodTick(v, unit)}
                />
                <YAxis stroke="var(--color-text-muted)" fontSize={11} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  itemSorter={(item) => rankOf(item.dataKey)}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  itemSorter={(item) => rankOf(item.dataKey)}
                />
                {SEVERITY_ORDER.map((level) => (
                  <Bar
                    key={level.key}
                    dataKey={level.key}
                    stackId="severity"
                    name={t(level.labelKey)}
                    fill={level.color}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('reliability.severity.empty')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
